import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Trash2, Warehouse, X } from "lucide-react";
import DockDoorSettings from "../TabContent/Calendar-Main/DockDoorSettings";

const EditDockModal = ({
  dock,
  onClose,
  onSave,
  onDelete,
  isSaving = false,
  isDeleting = false,
  onSaveComplete,
}) => {
  const [dockName, setDockName] = useState(dock?.dockName || "");
  const [settings, setSettings] = useState(
    dock?.settings || { regularHours: {}, holidays: [], blackouts: [], recurringEvents: [] }
  );
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const settingsSaveRef = useRef(null);

  useEffect(() => {
    setDockName(dock?.dockName || "");
    setSettings(
      dock?.settings || { regularHours: {}, holidays: [], blackouts: [], recurringEvents: [] }
    );
  }, [dock]);

  if (!dock) return null;

  const disableActions = isSaving || isDeleting || isSavingSettings;

  const handleSave = async () => {
    const settingsResult = settingsSaveRef.current
      ? await settingsSaveRef.current()
      : true;

    if (settingsResult === false) return;

    await onSave({
      dockName,
      settings,
    });
    
    if (onSaveComplete) {
      await onSaveComplete();
    }
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/40 z-[1000] flex items-center justify-center px-fluid-3 py-fluid-3"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="
            relative
            bg-white rounded-2xl shadow-xl
            w-full
            max-w-[95vw]
            md:max-w-[800px]
            lg:max-w-[1000px]
            xl:max-w-[1200px]
            2xl:max-w-[1400px]
            max-h-[95vh]
            overflow-y-auto
            p-fluid-4 space-y-fluid-4
            border border-gray-200
        "
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={disableActions}
          aria-label="Close"
          className="absolute top-fluid-3 right-fluid-3 p-1.5 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <X size={20} />
        </button>

        {/* ================= HEADER ================= */}
        <div className="flex flex-col items-center space-y-fluid-2 border-b pb-fluid-3">
          <div className="bg-[#1e2a74] rounded-full p-fluid-2 text-white shadow-md">
            <Warehouse size={30} />
          </div>

          <h2 className="text-fluid-xl font-semibold text-[#111827]">
            Edit Dock
          </h2>

          <p className="text-fluid-base text-gray-600 text-center max-w-[620px]">
            Update the dock name and configure its scheduling & capacity settings.
          </p>
        </div>

        {/* ================= MAIN FORM ================= */}
        <div className="space-y-fluid-4">
          <div className="grid grid-cols-[1fr,1fr,1fr] gap-fluid-3 items-center">
            <div className="space-y-fluid-1">
              <input
                type="text"
                value={dockName}
                onChange={(e) => setDockName(e.target.value)}
                className="
                  w-full border border-gray-300 rounded-xl 
                  p-fluid-2 text-fluid-sm
                  focus:outline-none focus:ring-2 focus:ring-[#223688]
                  shadow-sm
                "
                disabled={disableActions}
                placeholder="Enter dock name"
              />
            </div>

            <div className="col-span-2 text-fluid-sm text-gray-500">
            
            </div>
          </div>

          <div className="border rounded-xl p-fluid-3 bg-white shadow-sm">
            <DockDoorSettings
              settings={settings}
              onChange={(newSettings) => setSettings(newSettings)}
              isViewOnly={false}
            isWarehouseView={false}
            isNewDock={false}
            disableTabRedirect
            registerHandleSave={(fn) => {
              settingsSaveRef.current = fn;
            }}
            onSaveComplete={() => {}}
            onSettingsSavingChange={setIsSavingSettings}
            />
          </div>
        </div>

        {/* ================= FOOTER ================= */}
        <div className="flex items-center justify-between gap-fluid-3 pt-fluid-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-semibold text-fluid-sm"
            disabled={disableActions}
          >
            <Trash2 size={16} />
            {isDeleting ? "Deleting..." : "Delete dock"}
          </button>

          <div className="flex items-center gap-fluid-3">
            <button
              type="button"
              onClick={onClose}
              className="px-fluid-button-x py-fluid-button-y border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 text-fluid-sm transition"
              disabled={disableActions}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className={`
              px-fluid-button-x py-fluid-button-y 
              text-fluid-sm rounded-lg font-semibold transition
              ${dockName.trim() && !disableActions
                ? "bg-[#1e2a74] hover:bg-[#18225c] text-white"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }
            `}
              disabled={!dockName.trim() || disableActions}
            >
              {isSaving || isSavingSettings ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default EditDockModal;
